import type {
  CreateTransportTypeInput,
  PatchTransportTypeInput,
  TransportType,
  TransportTypeCategory,
} from '@magimp/shared';

export type DraftForm = {
  code: string;
  category: TransportTypeCategory;
  nameRu: string;
  nameEn: string;
  description: string;
  volumeMin: string;
  volumeMax: string;
  payloadMin: string;
  payloadMax: string;
  sortOrder: string;
};

export function emptyForm(): DraftForm {
  return {
    code: '',
    category: 'truck',
    nameRu: '',
    nameEn: '',
    description: '',
    volumeMin: '',
    volumeMax: '',
    payloadMin: '',
    payloadMax: '',
    sortOrder: '0',
  };
}

export function formFromRow(row: TransportType): DraftForm {
  return {
    code: row.code,
    category: row.category,
    nameRu: row.nameRu,
    nameEn: row.nameEn,
    description: row.description,
    volumeMin: row.volumeMinM3.toString(),
    volumeMax: row.volumeMaxM3.toString(),
    payloadMin: row.payloadMinT.toString(),
    payloadMax: row.payloadMaxT.toString(),
    sortOrder: row.sortOrder.toString(),
  };
}

export function parseDecimal(value: string): number {
  // WHY: form inputs are typed; an empty string parses to NaN here, which
  // the boundary `Number.isFinite` guard rejects so the API call never
  // ships an unparseable bound.
  const trimmed = value.trim().replace(',', '.');
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : Number.NaN;
}

export function buildCreate(form: DraftForm): CreateTransportTypeInput | null {
  const volumeMin = parseDecimal(form.volumeMin);
  const volumeMax = parseDecimal(form.volumeMax);
  const payloadMin = parseDecimal(form.payloadMin);
  const payloadMax = parseDecimal(form.payloadMax);
  const sortOrder = Number.parseInt(form.sortOrder, 10);
  if (![volumeMin, volumeMax, payloadMin, payloadMax].every((n) => Number.isFinite(n) && n >= 0)) {
    return null;
  }
  if (!Number.isFinite(sortOrder)) return null;
  return {
    code: form.code.trim(),
    category: form.category,
    nameRu: form.nameRu.trim(),
    nameEn: form.nameEn.trim(),
    description: form.description.trim(),
    volumeMinM3: volumeMin,
    volumeMaxM3: volumeMax,
    payloadMinT: payloadMin,
    payloadMaxT: payloadMax,
    active: true,
    sortOrder,
  };
}

export function diffPatch(prev: TransportType, form: DraftForm): PatchTransportTypeInput | null {
  const patch: Record<string, unknown> = {};
  if (form.category !== prev.category) patch.category = form.category;
  const nameRu = form.nameRu.trim();
  if (nameRu !== prev.nameRu) patch.nameRu = nameRu;
  const nameEn = form.nameEn.trim();
  if (nameEn !== prev.nameEn) patch.nameEn = nameEn;
  const description = form.description.trim();
  if (description !== prev.description) patch.description = description;
  const volumeMin = parseDecimal(form.volumeMin);
  if (Number.isFinite(volumeMin) && volumeMin !== prev.volumeMinM3) patch.volumeMinM3 = volumeMin;
  const volumeMax = parseDecimal(form.volumeMax);
  if (Number.isFinite(volumeMax) && volumeMax !== prev.volumeMaxM3) patch.volumeMaxM3 = volumeMax;
  const payloadMin = parseDecimal(form.payloadMin);
  if (Number.isFinite(payloadMin) && payloadMin !== prev.payloadMinT)
    patch.payloadMinT = payloadMin;
  const payloadMax = parseDecimal(form.payloadMax);
  if (Number.isFinite(payloadMax) && payloadMax !== prev.payloadMaxT)
    patch.payloadMaxT = payloadMax;
  const sortOrder = Number.parseInt(form.sortOrder, 10);
  if (Number.isFinite(sortOrder) && sortOrder !== prev.sortOrder) patch.sortOrder = sortOrder;
  if (Object.keys(patch).length === 0) return null;
  return patch as PatchTransportTypeInput;
}

// WHY: meaningful dirty for create — trim string fields and compare
// against the empty-form baseline. The `sortOrder: '0'` default stays
// the baseline, so opening the modal and immediately closing it does
// not trip the guard.
export function isCreateFormDirty(form: DraftForm): boolean {
  const baseline = emptyForm();
  return (
    form.code.trim() !== baseline.code ||
    form.category !== baseline.category ||
    form.nameRu.trim() !== baseline.nameRu ||
    form.nameEn.trim() !== baseline.nameEn ||
    form.description.trim() !== baseline.description ||
    form.volumeMin.trim() !== baseline.volumeMin ||
    form.volumeMax.trim() !== baseline.volumeMax ||
    form.payloadMin.trim() !== baseline.payloadMin ||
    form.payloadMax.trim() !== baseline.payloadMax ||
    form.sortOrder.trim() !== baseline.sortOrder
  );
}
